import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setAddToCartCardKeyToProcess, setOpenTab, setView } from '../../store/actions';
import { getKeyFromStorageAsync, saveKeyToStorageAsync } from '../../store/savedState';
import { getPendingAddToCartCardKey } from '../../store/selectors';
import { View } from '../../store/typings';
import { ADD_TO_CART_CLICKED_KEYS } from './contants';

const ADDING_MIN_DURATION_MS = 2000;
const ADDED_DURATION_MS = 3000;

export type AddToCartButtonState = 'add' | 'adding' | 'added' | 'cart';

interface UseAddToCartOptions {
    displayedVariantId: number; // Variant ID shown on the product card. Used to build a unique storage key for the card, regardless of which variant the user actually adds to cart.
    messageId: string;
    onAddToCart: (variantId: number) => Promise<boolean>;
    onViewCartClick: () => void;
    isFlyMessage?: boolean;
}

interface UseAddToCartReturn {
    buttonState: AddToCartButtonState;
    executeAddToCart: (variantId: number) => Promise<boolean>;
    handleViewCart: () => void;
}

const useAddToCart = ({
    displayedVariantId,
    messageId,
    isFlyMessage,
    onAddToCart,
    onViewCartClick,
}: UseAddToCartOptions): UseAddToCartReturn => {
    const dispatch = useDispatch();
    const pendingAddToCartCardKey = useSelector(getPendingAddToCartCardKey);
    const cardKey = `${messageId}_${displayedVariantId}`;
    const [buttonState, setButtonState] = useState<AddToCartButtonState>('add');
    const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();

    const handleAddToCartInFlyMessage = useCallback((): void => {
        dispatch(setView(View.CHAT));
        dispatch(setOpenTab('conversations'));
        dispatch(setAddToCartCardKeyToProcess(cardKey));
    }, [dispatch, cardKey]);

    const executeAddToCart = useCallback(
        async (selectedVariantId: number): Promise<boolean> => {
            if (isFlyMessage) {
                handleAddToCartInFlyMessage();
                return false;
            }

            setButtonState('adding');
            const [isAddedToCart] = await Promise.all([
                onAddToCart(selectedVariantId),
                new Promise((resolve) => {
                    setTimeout(resolve, ADDING_MIN_DURATION_MS);
                }),
            ]);

            if (!isAddedToCart) {
                setButtonState('add');
                return false;
            }

            let newStoredCardKeys: string[] = [];

            await getKeyFromStorageAsync(ADD_TO_CART_CLICKED_KEYS).then((storedCardKeys) => {
                if (Array.isArray(storedCardKeys)) {
                    newStoredCardKeys = [...storedCardKeys, cardKey];
                } else {
                    newStoredCardKeys = [cardKey];
                }
            });

            await saveKeyToStorageAsync(ADD_TO_CART_CLICKED_KEYS, newStoredCardKeys);
            setButtonState('added');

            timeoutIdRef.current = setTimeout(() => {
                timeoutIdRef.current = undefined;
                setButtonState('cart');
            }, ADDED_DURATION_MS);

            return true;
        },
        [handleAddToCartInFlyMessage, onAddToCart, isFlyMessage, cardKey],
    );

    const handleViewCart = useCallback((): void => {
        try {
            onViewCartClick();
            if (window.top) {
                const { origin } = window.top.location;
                window.top.location.assign(`${origin}/cart`);
            }
        } catch {
            //
        }
    }, [onViewCartClick]);

    useEffect(() => {
        if (!isFlyMessage && pendingAddToCartCardKey !== null) {
            dispatch(setAddToCartCardKeyToProcess(null));

            if (pendingAddToCartCardKey === cardKey) {
                executeAddToCart(displayedVariantId);
            }
        }
    }, [
        pendingAddToCartCardKey,
        cardKey,
        dispatch,
        executeAddToCart,
        isFlyMessage,
        displayedVariantId,
    ]);

    useEffect(() => {
        getKeyFromStorageAsync(ADD_TO_CART_CLICKED_KEYS).then((storedCardKeys) => {
            if (Array.isArray(storedCardKeys) && storedCardKeys.includes(cardKey)) {
                setButtonState('cart');
            }
        });
    }, [cardKey]);

    useEffect(
        () => (): void => {
            if (timeoutIdRef.current !== undefined) {
                clearTimeout(timeoutIdRef.current);
                timeoutIdRef.current = undefined;
            }
        },
        [],
    );

    return { buttonState, executeAddToCart, handleViewCart };
};

export default useAddToCart;
